import React, { useState } from 'react';
import { toast } from 'sonner';
import { SUPPORT_ID_FIELDS } from '../constants/release';

import {
  Check,
  Copy,
  Files,
  Info
} from '@phosphor-icons/react';
/**
 * Support ID Display Component
 * Shows User ID and Org ID with copy-to-clipboard functionality
 */
const SupportIdDisplay = ({ userId, organizationId, variant = 'full' }) => {
  const [copiedUser, setCopiedUser] = useState(false);
  const [copiedOrg, setCopiedOrg] = useState(false);
  const [copiedBoth, setCopiedBoth] = useState(false);

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'user') {
        setCopiedUser(true);
        setTimeout(() => setCopiedUser(false), 2000);
      } else if (type === 'org') {
        setCopiedOrg(true);
        setTimeout(() => setCopiedOrg(false), 2000);
      } else if (type === 'both') {
        setCopiedBoth(true);
        setTimeout(() => setCopiedBoth(false), 2000);
      }
      toast.success(`${type === 'user' ? 'User ID' : type === 'org' ? 'Organisation ID' : 'Support details'} copied to clipboard`);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const copyBothIds = async () => {
    const supportDetails = `User ID: ${userId}\nOrganisation ID: ${organizationId}`;
    await copyToClipboard(supportDetails, 'both');
  };

  // Don't render if no IDs available (user not authenticated)
  if (!userId && !organizationId) {
    return null;
  }

  if (variant === 'compact') {
    // For contact forms (auto-populated, smaller)
    return (
      <div className="bg-[#1A1B23] border border-[#2D2E3A] rounded-lg p-4 space-y-2">
        <div className="flex items-center gap-2 text-sm text-[#A0AEC0] mb-2">
          <Info size={14} />
          <span>When contacting support, please include the details below to help us assist you faster.</span>
        </div>
        {userId && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#718096]">User ID:</span>
            <code className="text-xs font-mono text-white">{userId}</code>
          </div>
        )}
        {organizationId && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#718096]">Organisation ID:</span>
            <code className="text-xs font-mono text-white">{organizationId}</code>
          </div>
        )}
      </div>
    );
  }

  // Full version for Account page
  return (
    <div className="bg-[#1A1B23] border border-[#2D2E3A] rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">Support Information</h3>
          <p className="text-sm text-[#A0AEC0]">
            Include these identifiers when contacting support for faster assistance.
          </p>
        </div>
        {userId && organizationId && (
          <button
            onClick={copyBothIds}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#6366F1]/10 hover:bg-[#6366F1]/20 text-[#6366F1] transition-colors text-sm font-medium"
            title="Copy both IDs"
          >
            {copiedBoth ? <Check size={16} /> : <Files size={16} />}
            <span className="hidden sm:inline">Copy both</span>
          </button>
        )}
      </div>
      
      <div className="space-y-4">
        {/* User ID */}
        {userId && (
          <div>
            <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
              User ID
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-[#161B26] border border-[#2D2E3A] rounded-lg text-sm font-mono text-white">
                {userId}
              </code>
              <button
                onClick={() => copyToClipboard(userId, 'user')}
                className="p-2 rounded-lg bg-[#6366F1]/10 hover:bg-[#6366F1]/20 text-[#6366F1] transition-colors"
                title="Copy User ID"
              >
                {copiedUser ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </div>
        )}

        {/* Organisation ID */}
        {organizationId && (
          <div>
            <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
              Organisation ID
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-[#161B26] border border-[#2D2E3A] rounded-lg text-sm font-mono text-white">
                {organizationId}
              </code>
              <button
                onClick={() => copyToClipboard(organizationId, 'org')}
                className="p-2 rounded-lg bg-[#6366F1]/10 hover:bg-[#6366F1]/20 text-[#6366F1] transition-colors"
                title="Copy Organisation ID"
              >
                {copiedOrg ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-start gap-2 text-xs text-[#718096]">
        <Info size={14} className="flex-shrink-0 mt-0.5" />
        <span>
          These identifiers are read-only and help our support team locate your account quickly.
        </span>
      </div>
    </div>
  );
};

export default SupportIdDisplay;
